/**
 * FilterCondition Component
 * 
 * Single filter row with field selector, operator selector, and value input.
 * 
 * NOTE: Contact Types and Tags are passed from admin-configured CRM Settings
 * to ensure filters use the same options as the rest of the application.
 */

import React, { useMemo } from 'react';
import {
  HStack,
  Select,
  Input,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiX } from 'react-icons/fi';
import DateRangeFilter from './DateRangeFilter';
import NumericRangeFilter from './NumericRangeFilter';
import MultiSelectFilter from './MultiSelectFilter';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';

/**
 * Build contact field definitions with admin-configured options
 */
function getContactFields(contactTypes = [], contactTags = [], campaignOptions = []) {
  return [
    { id: 'name', label: 'Name', type: 'text' },
    { id: 'email', label: 'Email', type: 'text' },
    { id: 'phone', label: 'Phone', type: 'text' },
    { id: 'company', label: 'Company', type: 'text' },
    { id: 'contactTypes', label: 'Contact Type', type: 'array', options: contactTypes },
    { id: 'tags', label: 'Tags', type: 'array', options: contactTags },
    // Currently enrolled campaigns (mirror of queue status pending/processing)
    { id: 'activeCampaigns', label: 'Enrolled in Campaign', type: 'array', options: campaignOptions },
    { id: 'source', label: 'Source', type: 'text' },
    { id: 'isActive', label: 'Active', type: 'boolean' },
    { id: 'isFavorite', label: 'Favorite', type: 'boolean' },
    { id: 'dnc', label: 'DNC: Everything', type: 'boolean' },
    { id: 'dncCall', label: 'DNC: Call', type: 'boolean' },
    { id: 'dncEmail', label: 'DNC: Email', type: 'boolean' },
    { id: 'dncSms', label: 'DNC: SMS', type: 'boolean' },
    { id: 'totalDeals', label: 'Total Deals', type: 'number' },
    { id: 'lifetimeValue', label: 'Lifetime Value', type: 'number' },
    { id: 'createdAt', label: 'Created Date', type: 'date' },
    { id: 'mailingState', label: 'State', type: 'text' },
  ];
}

/**
 * Build deal field definitions with admin-configured options
 */
function getDealFields(dealTags = [], dealStages = [], campaignOptions = [], dealPartyRoles = []) {
  // Use admin-configured stages if available, otherwise fall back to the
  // Arive-aligned default list (must stay in sync with src/constants/crmConstants.js).
  const stageOptions = dealStages.length > 0 ? dealStages : [
    { value: 'lead-new',                  label: 'New Lead' },
    { value: 'lead-contacted',            label: 'Contacted' },
    { value: 'lead-quoted',               label: 'Quoted' },
    { value: 'loan-application-intake',   label: 'Application Intake' },
    { value: 'loan-qualification',        label: 'Qualification' },
    { value: 'loan-preapproved',          label: 'Pre-Approved' },
    { value: 'loan-setup',                label: 'Loan Setup' },
    { value: 'loan-disclosure-sent',      label: 'Disclosure Sent' },
    { value: 'loan-submitted',            label: 'UW Submitted' },
    { value: 'loan-approved-conditions',  label: 'Approved w/ Conditions' },
    { value: 'loan-resubmittal',          label: 'Re-Submittal' },
    { value: 'loan-clear-to-close',       label: 'Clear to Close' },
    { value: 'loan-docs-out',             label: 'Docs Out' },
    { value: 'loan-docs-signed',          label: 'Docs Signed' },
    { value: 'loan-funded',               label: 'Funded' },
    { value: 'loan-brokercheck-received', label: 'Broker Check Received' },
    { value: 'loan-finalized',            label: 'Finalized' },
    { value: 'deal-lost',                 label: 'Lost' },
  ];
  
  return [
    { id: 'dealName', label: 'Deal Name', type: 'text' },
    { id: 'stage', label: 'Stage', type: 'select', options: stageOptions },
    { id: 'amount', label: 'Amount', type: 'number' },
    { id: 'source', label: 'Source', type: 'text' },
    { id: 'product', label: 'Product', type: 'text' },
    { id: 'loanNumber', label: 'Loan Number', type: 'text' },
    { id: 'lender', label: 'Lender', type: 'text' },
    { id: 'propertyState', label: 'Property State', type: 'text' },
    { id: 'propertyCity', label: 'Property City', type: 'text' },
    { id: 'propertyType', label: 'Property Type', type: 'text' },
    { id: 'occupancyType', label: 'Occupancy Type', type: 'select', options: [
      { value: 'primary_residence', label: 'Primary Residence' },
      { value: 'second_home', label: 'Second Home' },
      { value: 'investment', label: 'Investment Property' },
    ]},
    { id: 'loanPurpose', label: 'Loan Purpose', type: 'select', options: [
      { value: 'Purchase', label: 'Purchase' },
      { value: 'Rate/Term Refinance', label: 'Rate/Term Refinance' },
      { value: 'Cash Out', label: 'Cash Out' },
    ]},
    { id: 'tags', label: 'Tags', type: 'array', options: dealTags }, // Deal-specific tags
    // Deal Team roles present on the deal (crm_deals.deal_team_roles mirror,
    // maintained by DB trigger from crm_deal_participants)
    {
      id: 'dealTeamRoles',
      label: 'Deal Team Role',
      type: 'array',
      options: (dealPartyRoles || []).map(r => ({ value: r.value, label: r.label })),
    },
    // Currently enrolled campaigns — a deal matches when its contact is enrolled
    { id: 'activeCampaigns', label: 'Enrolled in Campaign', type: 'array', options: campaignOptions },
    { id: 'isAtRisk', label: 'At Risk', type: 'boolean' },
    { id: 'dnc', label: 'DNC: Everything', type: 'boolean' },
    { id: 'dncCall', label: 'DNC: Call', type: 'boolean' },
    { id: 'dncEmail', label: 'DNC: Email', type: 'boolean' },
    { id: 'dncSms', label: 'DNC: SMS', type: 'boolean' },
    { id: 'creditScore', label: 'Credit Score', type: 'number' },
    { id: 'downPayment', label: 'Down Payment %', type: 'number' },
    { id: 'createdAt', label: 'Created Date', type: 'date' },
    { id: 'estimatedClosingDate', label: 'Est. Closing Date', type: 'date' },
    { id: 'nextActionDue', label: 'Next Action Due', type: 'date' },
  ];
}

// Operators by field type
const OPERATORS_BY_TYPE = {
  text: [
    { value: 'contains', label: 'Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ],
  number: [
    { value: 'eq', label: 'Equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'gte', label: 'Greater than or equal' },
    { value: 'lt', label: 'Less than' },
    { value: 'lte', label: 'Less than or equal' },
    { value: 'between', label: 'Between' },
    { value: 'is_empty', label: 'Is empty' },
  ],
  date: [
    { value: 'date_eq', label: 'Is' },
    { value: 'date_before', label: 'Before' },
    { value: 'date_after', label: 'After' },
    { value: 'date_between', label: 'Between' },
    { value: 'date_relative', label: 'In the last...' },
    { value: 'is_empty', label: 'Is empty' },
  ],
  select: [
    { value: 'in', label: 'Is any of' },
    { value: 'not_in', label: 'Is not any of' },
    { value: 'is_empty', label: 'Is empty' },
  ],
  array: [
    { value: 'contains_any', label: 'Contains any of' },
    { value: 'contains_all', label: 'Contains all of' },
    { value: 'not_contains_any', label: 'Does not contain any of' },
    { value: 'array_is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ],
  boolean: [
    { value: 'is_true', label: 'Is Yes' },
    { value: 'is_false', label: 'Is No' },
  ],
};

/**
 * FilterCondition - Single filter row
 * 
 * @param {Object} filter - Current filter state
 * @param {Array} fieldDefinitions - Custom field definitions (optional)
 * @param {string} entityType - 'contacts' or 'deals'
 * @param {Array} contactTypes - Admin-configured contact types
 * @param {Array} contactTags - Admin-configured tags for contacts (from Supabase)
 * @param {Array} dealTags - Admin-configured tags for deals (from Supabase)
 * @param {Array} dealStages - Admin-configured deal stages
 * @param {Array} campaignOptions - Active campaign templates ({ value, label, activeCount })
 * @param {Function} onChange - Callback when filter changes
 * @param {Function} onRemove - Callback to remove this filter
 */
export default function FilterCondition({
  filter,
  fieldDefinitions,
  entityType = 'contacts',
  contactTypes = [],
  contactTags = [],
  dealTags = [],
  dealStages = [],
  campaignOptions = [],
  onChange,
  onRemove,
}) {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.700');
  // Deal Team role options come straight from admin-configured settings
  // (provider is mounted at the app root, so this is always available)
  const { dealPartyRoles } = useCrmSettings();
  
  // Get fields based on entity type, using admin-configured options
  // Use separate tag lists for contacts vs deals
  const fields = useMemo(() => {
    if (fieldDefinitions?.length > 0) {
      return fieldDefinitions;
    }
    
    if (entityType === 'deals') {
      return getDealFields(dealTags, dealStages, campaignOptions, dealPartyRoles);
    }
    
    return getContactFields(contactTypes, contactTags, campaignOptions);
  }, [fieldDefinitions, entityType, contactTypes, contactTags, dealTags, dealStages, campaignOptions, dealPartyRoles]);
  
  // Get selected field definition
  const selectedField = useMemo(() => {
    return fields.find(f => f.id === filter.field);
  }, [fields, filter.field]);
  
  // Get available operators for selected field type
  const availableOperators = useMemo(() => {
    if (!selectedField) return [];
    return OPERATORS_BY_TYPE[selectedField.type] || OPERATORS_BY_TYPE.text;
  }, [selectedField]);
  
  // Handle field change
  const handleFieldChange = (e) => {
    const newField = e.target.value;
    onChange({
      ...filter,
      field: newField,
      operator: '', // Reset operator when field changes
      value: '',    // Reset value when field changes
    });
  };
  
  // Handle operator change
  const handleOperatorChange = (e) => {
    const newOperator = e.target.value;
    onChange({
      ...filter,
      operator: newOperator,
      value: '', // Reset value when operator changes
    });
  };
  
  // Handle value change
  const handleValueChange = (newValue) => {
    onChange({
      ...filter,
      value: newValue,
    });
  };
  
  // Render value input based on field type and operator
  const renderValueInput = () => {
    if (!selectedField || !filter.operator) return null;
    
    // Operators that don't need a value input
    const noValueOperators = ['is_empty', 'is_not_empty', 'array_is_empty', 'is_true', 'is_false'];
    if (noValueOperators.includes(filter.operator)) {
      return null;
    }
    
    // Date fields
    if (selectedField.type === 'date') {
      return (
        <DateRangeFilter
          operator={filter.operator}
          value={filter.value}
          onChange={handleValueChange}
        />
      );
    }
    
    // Number fields
    if (selectedField.type === 'number') {
      return (
        <NumericRangeFilter
          operator={filter.operator}
          value={filter.value}
          onChange={handleValueChange}
        />
      );
    }
    
    // Select fields (single value options)
    if (selectedField.type === 'select' && selectedField.options) {
      return (
        <MultiSelectFilter
          options={selectedField.options}
          value={Array.isArray(filter.value) ? filter.value : (filter.value ? [filter.value] : [])}
          onChange={handleValueChange}
        />
      );
    }
    
    // Array fields (tags, contactTypes, etc.)
    if (selectedField.type === 'array' && selectedField.options) {
      return (
        <MultiSelectFilter
          options={selectedField.options}
          value={Array.isArray(filter.value) ? filter.value : []}
          onChange={handleValueChange}
        />
      );
    }
    
    // Default text input
    return (
      <Input
        size="sm"
        placeholder="Enter value..."
        value={filter.value || ''}
        onChange={(e) => handleValueChange(e.target.value)}
        flex={1}
        minW="150px"
      />
    );
  };
  
  return (
    <HStack
      spacing={2}
      p={2}
      bg={bgColor}
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
      flexWrap="wrap"
    >
      {/* Field Selector */}
      <Select
        size="sm"
        placeholder="Select field..."
        value={filter.field || ''}
        onChange={handleFieldChange}
        maxW="160px"
      >
        {fields.map(field => (
          <option key={field.id} value={field.id}>
            {field.label}
          </option>
        ))}
      </Select>
      
      {/* Operator Selector */}
      {filter.field && (
        <Select
          size="sm"
          placeholder="Select operator..."
          value={filter.operator || ''}
          onChange={handleOperatorChange}
          maxW="160px"
        >
          {availableOperators.map(op => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </Select>
      )}
      
      {/* Value Input */}
      {renderValueInput()}
      
      {/* Remove Button */}
      <IconButton
        icon={<FiX />}
        size="sm"
        variant="ghost"
        colorScheme="red"
        aria-label="Remove filter"
        onClick={onRemove}
      />
    </HStack>
  );
}
